// ============================================================
// Inventory module manifest — V2 platform's first canary.
//
// R2 (2026-04-23) — see ARCHITECTURE.md §2 (manifest format) and §9 (R2 phase).
// principal-architect owns the shape; firebase-expert wires it into Vite.
//
// This manifest is the SINGLE source of truth for:
//   - what routes the inventory module contributes to the sidebar,
//   - which Firestore collections it owns (R6 will generate Rules from here),
//   - which permission strings exist in the inventory namespace (R6),
//   - which dashboard widgets the module offers (R3+R4 wire them in),
//   - which industry packs include this module by default (manufacturing,
//     trading, retail; services pack omits inventory entirely per §4).
//
// IMPORTANT: changes to `id` are BREAKING — the id appears in tenantConfig
// .enabledModules + permission strings + URL hashes + parity matrix.
// Treat `id` as a stable contract.
// ============================================================

export default {
  id:          'inventory',
  name:        'Inventory',
  version:     '1.0.0',
  description: 'Stock master, levels, movements, reorder points, BOM cascade.',

  // Lazy load — dynamic import means Vite emits a separate chunk for this
  // module. Other tenants that don't enable `inventory` never download it.
  // (Empirically: a tenant on the Services pack ships ~30-40 kB less because
  // of this split — see PARITY R2 entry once measured.)
  load: () => import('./index.js'),

  // Routes contributed to the sidebar.
  // R2 (2026-04-23): only inventory list.
  // 2026-05-06: added dedicated Stock Movements page — staff + admin must
  //   see ALL movements (not just the dashboard-widget snapshot). BOM still
  //   lives behind a row click in the item modal, no separate route.
  routes: [
    {
      id:       'inventory',
      label:    'Inventory',
      icon:     '📦',
      section:  'main',
      sidebarPosition: 20,
      // 2026-05-04 sidebar-leak fix — gate by literal `inventory.read`
      // permission against the materialized rolesMap (R6 source of truth).
      // Manufacturing pack: director, admin, production-supervisor, shop-floor,
      // sales (sales gets inventory.read for the line picker). Staff role
      // does NOT get this — they no longer see Inventory in the sidebar.
      // The R6 fast-path (director/admin via '*' + __ALL_EXCEPT_TENANT_CONFIG__)
      // is preserved by hasPerm. Write/move gating still happens inside the page.
      visible:  (role, hasPerm) => hasPerm('inventory.read'),
      // Bound by string-name lookup against the loaded body (see registry.js).
      mount:    'mountInventoryList',
      unmount:  'unmountInventoryList'
    },
    {
      // 2026-05-06 — dedicated, tenant-wide Stock Movements audit view.
      // Sits right after Inventory under the Main section so the two
      // stock-centric pages cluster together (ux-architect's call).
      // Gated by `inventory.read` — the same permission Inventory uses, so
      // exactly the same audience sees it: director, admin, production-
      // supervisor, shop-floor, sales, AND staff (staff was granted
      // inventory.read on 2026-05-04 per the Staff role inventory grant
      // entry in PARITY.md). This grants staff visibility into ALL
      // movements (not just per-item), which is the user request.
      //
      // No additional Rules change: stockmovements collection already
      // allows read to any tenant member (firestore.rules:334), and the
      // schema carries no pricing — verified vs feedback_no_pricing_to_staff.md.
      id:       'stock-movements',
      label:    'Stock Movements',
      icon:     '📜',
      section:  'main',
      sidebarPosition: 30,
      visible:  (role, hasPerm) => hasPerm('inventory.read'),
      mount:    'mountStockMovements',
      unmount:  'unmountStockMovements'
    }
  ],

  // Firestore collections this module reads/writes. Expressed as plain
  // collection names; the actual paths are tenant-scoped via tenantCol().
  // R6 will use this list to generate the matching Rules block.
  schemas: [
    'inventory',         // master items
    'stockmovements',    // append-only movement log
    'inventorybom',      // bill-of-materials rows
    'componenttypes'     // dropdown taxonomy for the Component field
  ],

  // Permission strings the module declares. R6 maps roles → permissions
  // per industry; today the UI checks role directly (canManageInventory).
  permissions: [
    'inventory.read',     // see the inventory list
    'inventory.write',    // create / edit / archive items
    'inventory.move',     // post stock movements (Stock-In / Stock-Out)
    'inventory.delete'    // permanent delete (director/admin only)
  ],

  // Other modules that must be enabled BEFORE this one. Inventory has none —
  // it's a foundation for other modules (BOM, procurement, sales).
  dependencies: [],

  // Industry packs that include this module by default (ARCHITECTURE.md §4).
  // Services pack intentionally omits inventory.
  fitsIndustries: ['manufacturing', 'trading', 'retail'],

  // Cloud Functions this module needs (Blaze-plan deployments only).
  // None today — all writes are direct client→Firestore via writeBatch.
  functions: [],

  // Dashboard widgets — wired by the manifest-driven dashboard composition
  // (R3, see src/modules/dashboard/pages/dashboard.js).
  // section: 'kpi' = top KPI strip; 'left-col' = left column; 'right-col' = right column.
  // order:   lower number = higher placement within its section.
  dashboardWidgets: [
    {
      id:           'inventory-total-items-kpi',
      label:        'Total items',
      section:      'kpi',
      order:        10,
      defaultRoles: ['director', 'admin', 'manager', 'inventory', 'shop-floor']
    },
    {
      id:           'inventory-low-stock-kpi',
      label:        'Low stock',
      section:      'kpi',
      order:        20,
      defaultRoles: ['director', 'admin', 'manager', 'inventory']
    },
    {
      id:           'inventory-out-of-stock-kpi',
      label:        'Out of stock',
      section:      'kpi',
      order:        30,
      defaultRoles: ['director', 'admin', 'manager', 'inventory']
    },
    {
      id:           'inventory-value-kpi',
      label:        'Inv. value',
      section:      'kpi',
      order:        40,
      defaultRoles: ['director', 'admin', 'manager']
    },
    {
      id:           'inventory-movements-today-kpi',
      label:        "Today's movements",
      section:      'kpi',
      order:        100,
      defaultRoles: ['director', 'admin', 'manager', 'inventory', 'shop-floor']
    },
    {
      id:           'inventory-low-stock-list',
      label:        'Items needing reorder',
      section:      'left-col',
      order:        10,
      defaultRoles: ['director', 'admin', 'manager', 'inventory']
    },
    {
      id:           'inventory-recent-movements-list',
      label:        'Recent stock movements',
      section:      'right-col',
      order:        10,
      defaultRoles: ['director', 'admin', 'manager', 'inventory', 'shop-floor']
    }
  ]
};
